/**
 * Suggested donation tiers stored on `funding_appeals.donation_tiers` (JSONB).
 */

export type FundingDonationTier = {
  id: string;
  label: string;
  amount_cents: number;
  description: string;
  highlight?: boolean;
};

export const MAX_FUNDING_DONATION_TIERS = 10;

export const DEFAULT_FUNDING_DONATION_TIERS: FundingDonationTier[] = [
  {
    id: "personal-supporter",
    label: "Personal supporter",
    amount_cents: 2500,
    description: "Every gift helps us reach our goal.",
    highlight: false,
  },
  {
    id: "company-supporter",
    label: "Company supporter",
    amount_cents: 10000,
    description: "Show your organisation's support with a larger contribution.",
    highlight: true,
  },
  {
    id: "premium-sponsor",
    label: "Premium sponsor",
    amount_cents: 50000,
    description: "Become a named sponsor and make a major impact.",
    highlight: false,
  },
];

function stableTierId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `tier-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function parseTierRow(raw: unknown): FundingDonationTier | null {
  if (!isPlainObject(raw)) return null;
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  const amount_cents =
    typeof raw.amount_cents === "number" && Number.isFinite(raw.amount_cents)
      ? Math.round(raw.amount_cents)
      : NaN;
  if (!label || !Number.isFinite(amount_cents) || amount_cents <= 0) return null;
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : stableTierId();
  return {
    id,
    label,
    amount_cents,
    description,
    highlight: raw.highlight === true,
  };
}

/** Normalize appeal JSON into a validated tier list; applies defaults when empty. */
export function normalizeFundingDonationTiers(
  raw: unknown,
  options?: { applyDefaults?: boolean },
): FundingDonationTier[] {
  const applyDefaults = options?.applyDefaults !== false;
  if (raw == null) return applyDefaults ? [...DEFAULT_FUNDING_DONATION_TIERS] : [];

  const rows = Array.isArray(raw) ? raw : [];
  const tiers: FundingDonationTier[] = [];
  for (const row of rows) {
    const tier = parseTierRow(row);
    if (tier) tiers.push(tier);
    if (tiers.length >= MAX_FUNDING_DONATION_TIERS) break;
  }

  if (tiers.length === 0 && applyDefaults) return [...DEFAULT_FUNDING_DONATION_TIERS];
  return tiers;
}

/** Ensure each tier has an id and trimmed strings before persisting. */
export function prepareFundingDonationTiersForSave(raw: unknown): FundingDonationTier[] {
  const tiers = normalizeFundingDonationTiers(raw, { applyDefaults: true });
  return tiers.map((tier) => ({
    id: tier.id.trim() || stableTierId(),
    label: tier.label.trim(),
    amount_cents: Math.round(tier.amount_cents),
    description: tier.description.trim(),
    ...(tier.highlight ? { highlight: true } : {}),
  }));
}

export function validateFundingDonationTiersForSave(tiers: FundingDonationTier[]): void {
  if (tiers.length === 0) throw new Error("Add at least one donation tier.");
  if (tiers.length > MAX_FUNDING_DONATION_TIERS) {
    throw new Error(`At most ${MAX_FUNDING_DONATION_TIERS} donation tiers are allowed.`);
  }
  for (const [index, tier] of tiers.entries()) {
    if (!tier.label.trim()) throw new Error(`Tier ${index + 1}: label is required.`);
    if (!tier.description.trim()) throw new Error(`Tier ${index + 1}: description is required.`);
    if (!Number.isFinite(tier.amount_cents) || tier.amount_cents <= 0) {
      throw new Error(`Tier ${index + 1}: amount must be a positive number of cents.`);
    }
  }
}
